import React, { useEffect } from "react";
import { Grid } from "@mui/material";
import { CircularProgress } from "@mui/material";

import Player from "./Player";
import PlaySettings from "./PlaySettings";
import { useState, useRef } from "react";
import { cameraService } from "client-app-core";

import { useDispatch } from "react-redux";

import * as PlaySettingsActions from "orion-components/GlobalData/WowzaWebRTC/PlaySettings/actions";
import * as PlaySettingsActionTypes from "orion-components/GlobalData/WowzaWebRTC/PlaySettings/actionTypes";
import PropTypes from "prop-types";

import EventBus from "orion-components/SharedComponents/EventBus";
import StreamFailed from "./utils/StreamFailed";

const propTypes = {
	camera: PropTypes.object.isRequired,
	isForPlayback: PropTypes.bool,
	start: PropTypes.string,
	end: PropTypes.string,
	getPlaybackStreamName: PropTypes.func
};

const defaultProps = {
	camera: {},
	isForPlayback: false,
	start: "",
	end: ""
};

const areStringsNonEmpty = (stringsArray) => {
	return stringsArray.every((currentString) => {
		if (currentString) {
			return currentString.length > 0;
		} else {
			return false;
		}
	});
};

const WowzaWebRTCPlayer = ({ camera, start, end, isForPlayback, getPlaybackStreamName }) => {
	const dispatch = useDispatch();

	const wowzaStreamName = useRef("");
	const wowzaApplicationName = useRef("");
	const wowzaSignalingUrl = useRef("");
	const [isLoaded, setIsLoaded] = useState(false);
	const isNewVideoPlayer = camera.player?.newPlayer || false;
	const [wowzaStreamStatus, SetWowzaStreamStatus] = useState("Starting");

	const getWowzaStream = (retry = false) => {
		if (retry) {
			setStreamStatus("Starting");
		}
		const { id, player } = camera;
		//Logic for retrieving wowza webrtc information
		if (!isForPlayback || (isForPlayback && areStringsNonEmpty([start, end]))) {
			cameraService.getWowzaStream(id, isForPlayback, start, end, (err, response) => {
				if (err) {
					setStreamStatus("Failed");
					console.log("ERROR", err);
				}
				if (!response) {
					setStreamStatus("Failed");
					return;
				}

				if (response.data?.streamConnected) {
					const { streamConnected } = response.data;
					if (streamConnected) {
						const { streamName } = response.data;
						const { applicationName, url } = player;
						wowzaStreamName.current = streamName;
						wowzaApplicationName.current = applicationName;
						wowzaSignalingUrl.current = url;
						SetWowzaStreamStatus("Connected");
						if (isForPlayback && areStringsNonEmpty([start, end])) {
							EventBus.publish("getStreamToBeRemoved", streamName);
							getPlaybackStreamName(streamName);
						}
					} else {
						setStreamStatus("Failed");
					}
				} else {
					setStreamStatus("Failed");
				}
			});
		}
	};

	const setStreamStatus = (status) => {
		setIsLoaded(false);
		wowzaStreamName.current = "";
		wowzaApplicationName.current = "";
		wowzaSignalingUrl.current = "";
		SetWowzaStreamStatus(status);
	};

	useEffect(() => {
		if (isForPlayback && areStringsNonEmpty([start, end])) {
			getWowzaStream();
			setStreamStatus("Starting");
		} else {
			setStreamStatus("Starting");
			getWowzaStream();
		}
	}, [camera?.id, isForPlayback, start, end]);

	const setPlaySettings = () => {
		dispatch({
			type: PlaySettingsActionTypes.SET_PLAY_SETTINGS,
			signalingURL: wowzaSignalingUrl.current,
			streamName: wowzaStreamName.current,
			applicationName: wowzaApplicationName.current
		});
		dispatch(PlaySettingsActions.startPlay(wowzaStreamName.current));
	};
	const toggleLoading = () => {
		setIsLoaded(!isLoaded);
	};

	const streamStatus = () => {
		if (wowzaStreamStatus === "Starting") {
			return (
				<div
					style={{
						height: "100%",
						width: "100%",
						padding: "2.5%",
						textAlign: "center"
					}}
				>
					<CircularProgress />
				</div>
			);
		} else if (wowzaStreamStatus === "Failed") {
			return <StreamFailed retryStream={getWowzaStream} />;
		}

		return null;
	};

	return (
		<Grid
			container
			spacing={isNewVideoPlayer ? 0 : 3}
			sx={{ ...(isLoaded && { width: "100%", height: "100%" }) }}
			className="mt-3"
			id="WebRTCPlayer-play-content"
		>
			<Grid item xs={12} md={12} sm={12} lg={12} sx={{ ...(isLoaded && { width: "100%", height: "100%" }) }}>
				{isLoaded &&
				areStringsNonEmpty([
					wowzaStreamName.current,
					wowzaApplicationName.current,
					wowzaSignalingUrl.current
				]) &&
				wowzaStreamStatus === "Connected" ? (
					<Player
						getWowzaStream={getWowzaStream}
						setPlaySettings={setPlaySettings}
						isNewVideoPlayer={isNewVideoPlayer}
						wowzaStreamName={wowzaStreamName.current}
						wowzaApplicationName={wowzaApplicationName.current}
						wowzaSignalingUrl={wowzaSignalingUrl.current}
					/>
				) : (
					streamStatus()
				)}
			</Grid>
			<Grid item xs={12}>
				{areStringsNonEmpty([
					wowzaStreamName.current,
					wowzaApplicationName.current,
					wowzaSignalingUrl.current
				]) ? (
					<PlaySettings
						signalingURL={wowzaSignalingUrl.current}
						applicationName={wowzaApplicationName.current}
						streamName={wowzaStreamName.current}
						toggleLoading={toggleLoading}
					/>
				) : null}
			</Grid>
		</Grid>
	);
};

WowzaWebRTCPlayer.propTypes = propTypes;
WowzaWebRTCPlayer.defaultProps = defaultProps;

export default WowzaWebRTCPlayer;
